/**
 * MCP の接続の許可の画面の日本語（en.mcpAuth.ts と同じキーを全部持つ。漏れは typecheck で落ちる）。表記の決め事は ja.ts の冒頭と同じ。
 * ループバックの確認は「警告」の言葉を使わない（2026-09-28 calm-consent・founder「変に注意が出てくると怖い」）
 */
import type { enMcpAuth } from './en.mcpAuth';
import type { PluralMsg } from './index';

type McpAuthMessages = { [K in keyof typeof enMcpAuth]: (typeof enMcpAuth)[K] extends string ? string : string | PluralMsg };

export const jaMcpAuth: McpAuthMessages = {
  'mcpAuth.pageTitle': 'AI と連携',
  'mcpAuth.title': '{name} と連携しますか？',
  'mcpAuth.titleKnownWeb': '{app}（{host}）を {product} と連携しますか？',
  'mcpAuth.unnamed': 'この AI',
  'mcpAuth.signedInAs': '{email} でログイン中',
  'mcpAuth.notYou': '（別のアカウントに切り替える）',
  'mcpAuth.result.build': '連携すると、{name} があなたの代わりにアプリを作って公開できます。公開や削除の前には、毎回この画面で確認します。',
  'mcpAuth.result.read': '連携すると、{name} はあなたのアプリの様子を見られます。何も変更できません。',
  'mcpAuth.result.custom': '連携すると、{name} は「詳細」で選んだことだけできます。',
  'mcpAuth.safety': '心当たりがなければ「やめる」を押してください。何も変わりません。',
  'mcpAuth.lead': 'このエージェントに許可する操作を選んでください。接続はいつでも解除できます。',

  // verified は known_web と同じ落ち着いた言い方（2026-10-04 core/known-web・前は「確認済みのアプリ · {domain} が身元を確認」
  //   「自分のエージェントから接続を始めたときだけ…」= 硬い・中の言葉。安全の 1 行は見える所に別にある）
  'mcpAuth.identity.verified': '{domain} のアプリだと確かめました。',
  'mcpAuth.identity.verifiedNote': '連携の結果は、このアプリにだけ届きます。',
  'mcpAuth.identity.knownWeb': '連携の結果は {app} の公式のアドレス（{host}）にだけ届きます。',
  'mcpAuth.identity.loopback': 'いま、このパソコンの AI エージェント（名前は {name} と名乗っています）から接続を始めましたか？ そうなら続けてください。許可は {host}（このパソコンの中のアプリ）に送られます。',
  'mcpAuth.identity.remoteTitle': '確認されていないアプリです。',
  'mcpAuth.identity.remote': 'このアプリが本当に {name} かどうか、{product} は確かめられません（アプリの名前は誰でも自由に付けられます）。自分のアシスタントから接続を始めたばかりのときだけ続けてください。',
  'mcpAuth.identity.remoteTarget': '許可は {host}（あなたのパソコンではなく、インターネット上のサーバー）に送られます。心当たりがなければ中止してください。',

  'mcpAuth.permissions': '許可する操作',
  'mcpAuth.preset.build': '作って公開する（おすすめ）',
  'mcpAuth.preset.buildNote': 'すべてを見る・プロジェクトの設定を変える・公開とデプロイ・サーバー関数の管理ができます。本番への反映と削除は、そのたびに確認します。',
  'mcpAuth.preset.read': '見るだけ',
  'mcpAuth.preset.readNote': 'プロジェクト・利用者・売上・残高・サーバー関数とそのログを見られます。何も変えません。',
  'mcpAuth.preset.readNoteLaunch': 'プロジェクト・利用者・レポート・利用料と前払い残高・サーバー関数とそのログを見られます。何も変えません。',
  'mcpAuth.preset.custom': '自分で選ぶ',
  'mcpAuth.preset.customNote': '1つずつ選びます。出金の申請・メールの送信・利用者のBANは、ここでだけ足せます。',
  'mcpAuth.preset.customNoteLaunch': '1つずつ選びます。利用者のBANは、ここでだけ足せます。',
  'mcpAuth.customLegend': '許可する操作',
  'mcpAuth.tag.medium': '書き込み',
  'mcpAuth.tag.high': 'お金',
  'mcpAuth.projects': 'プロジェクト',
  'mcpAuth.projects.all': 'すべてのプロジェクト',
  'mcpAuth.projects.allNote': 'これから作るプロジェクトも含みます',
  'mcpAuth.projects.selected': '下でチェックしたプロジェクトだけ',
  'mcpAuth.projects.none': 'プロジェクトはまだありません。',
  // ボタンの上の 1 行（2026-10-14 consent-one-button・mcpAuthorizeProjects.ts）: 何が選ばれているか。{list} = 「すべてのプロジェクト」かプロジェクトの名前
  'mcpAuth.target': '対象: {list}',
  'mcpAuth.target.more': '{names} ほか {count} 件',
  'mcpAuth.target.none': 'まだ選ばれていません',
  'mcpAuth.target.change': '変える',
  'mcpAuth.withdrawalNote': '出金には必ず {product} のサイトでのパスキーの確認が要ります。受け取りの銀行口座はエージェントからは変更できません。',
  'mcpAuth.returnsTo': '戻り先: {host}',
  'mcpAuth.allow': '連携する',
  'mcpAuth.cancel': 'やめる',
  'mcpAuth.returning': 'エージェントに戻っています。画面が切り替わらないときは、このタブを閉じてかまいません。',

  'mcpAuth.error.noScope': '許可する操作を 1 つ以上選んでください。',
  'mcpAuth.error.noProject': 'プロジェクトを 1 つ以上選ぶか、「すべてのプロジェクト」を選んでください。',
  'mcpAuth.error.invalidLink': 'このリンクは正しい接続の要求ではありません。エージェントからもう一度接続を始めてください。',
  'mcpAuth.error.expired': 'この接続の要求は期限が切れたか、すでに使われています。エージェントからもう一度接続を始めてください。',
  // stepup-ux（2026-10-05・en.mcpAuth.ts と同じ）
  'mcpAuth.titleAdd': '{name} に許可を足しますか？',
  'mcpAuth.addLead': '{name} とはもう連携しています。今要る許可を足すだけで、連携はそのままです。',
  'mcpAuth.addNow': '今できること: {list}',
  'mcpAuth.addAdding': '足すこと: {list}',
  'mcpAuth.addAlready': '{name} にはもうこの許可があります。「許可する」を押すと続きます。',
  'mcpAuth.allowAdd': '許可する',
  'mcpAuth.listSep': '、',
  // consent-simplify（2026-10-24・en.mcpAuth.ts と同じ）
  'mcpAuth.simple.alreadyTitle': '{name} とはもう連携しています',
  'mcpAuth.simple.alreadyLead': '頼まれたことは、もうできるようになっています。「続ける」を押すと {name} に戻ります。',
  'mcpAuth.simple.continue': '続ける',
  'mcpAuth.simple.moreTitle': '{name} に「{list}」も許可しますか？',
  'mcpAuth.simple.moreLead': 'もう連携していて、今は「{list}」ができます。',
  'mcpAuth.simple.listLast': '、',
  'mcpAuth.simple.remote': 'アプリの名前は誰でも自由に付けられるので、本当に {name} かどうか {product} には確かめられません。自分の AI のアプリから、いま連携を始めたときだけ続けてください。',
  'mcpAuth.simple.target': '許可は {host}（インターネット上のサーバー）に送られます。',
  'mcpAuth.part.view': 'アプリを見る',
  'mcpAuth.part.settings': 'アプリの設定を変える',
  'mcpAuth.part.publish': 'アプリを公開する',
  'mcpAuth.part.functions': 'サーバーの関数を管理する',
  'mcpAuth.part.users': '利用者の利用を止める・戻す',
  'mcpAuth.part.mail': '利用者にメールを送る',
  'mcpAuth.part.withdraw': '出金を申請する',
  'mcpAuth.expired.chatgpt': 'このページは期限が切れました。ChatGPT のチャットに戻って、もう一度「再接続」を押してください。作業はそのまま続きます。',
  'mcpAuth.expired.claude': 'このページは期限が切れました。Claude のチャットに戻って続きを頼み、{product} と連携するボタンが出たら、もう一度押してください。作業はそのまま続きます。',
  'mcpAuth.expired.cursor': 'このページは期限が切れました。Cursor に戻って続きを頼み、{product} と連携するボタンが出たら、もう一度押してください。作業はそのまま続きます。',
  'mcpAuth.expired.muse': 'このページは期限が切れました。Muse の会話に戻って続きを頼み、{product} と連携するボタンが出たら、もう一度押してください。作業はそのまま続きます。',
  'mcpAuth.backTo': '{app} に戻る',
  'mcpAuth.backManual': 'ここからチャットに戻れませんでした。{app} のチャットが別のタブで開いていれば、そのタブに切り替えてください。無ければ {app} を開いて左の一覧から元のチャットを選び、そこでもう一度ボタンを押してください。',
  'mcpAuth.openApp': '{app} を開く',
  'mcpAuth.error.accountClosed': 'このアカウントは閉鎖されているため、AI エージェントを接続できません。',
  'mcpAuth.error.scopeNotRequested': '選んだ操作の中に、このエージェントが求めていないものがあります。画面を読み込み直して選び直してください。',
  'mcpAuth.error.badRedirect': '{product} から想定外のアドレスが返ったため、エージェントには何も送っていません。エージェントからもう一度接続を始めてください。',
  'mcpAuth.emailUnverified': 'AI と連携するには、メールアドレス（{email}）の確認が必要です。届いたメールのリンクを開いてから、AI からもう一度連携してください。',

  'mcpAuth.scope.read_projects.title': 'プロジェクトを見る',
  'mcpAuth.scope.read_projects.desc': 'プロジェクトの一覧、利用者の数、DAU、設定、デプロイの履歴、独自ドメイン',
  'mcpAuth.scope.read_reports.title': '売上のレポートを見る',
  'mcpAuth.scope.read_reports.desc': 'プロジェクト別・収益源別（購入・広告）の売上、CSV の書き出し、X の投稿（共有リンク）からの流入、メール配信の状況と結果',
  'mcpAuth.scope.read_players.title': '利用者を見る',
  'mcpAuth.scope.read_players.desc': '利用者の検索・詳細、BAN の異議申し立ての閲覧（メールアドレスは表示されません）',
  'mcpAuth.scope.read_payouts.title': '残高と出金を見る',
  'mcpAuth.scope.read_payouts.desc': '保留中・出金可能・支払い済みの残高、出金の履歴、出金の状況',
  'mcpAuth.scope.read_reports.titleLaunch': 'レポートを見る',
  'mcpAuth.scope.read_reports.descLaunch': 'X の投稿（共有リンク）からの流入、メール配信の状況と結果',
  'mcpAuth.scope.read_reports.descNoMail': 'プロジェクト別・収益源別（購入・広告）の売上、CSV の書き出し、X の投稿（共有リンク）からの流入',
  'mcpAuth.scope.read_reports.descLaunchNoMail': 'X の投稿（共有リンク）からの流入',
  'mcpAuth.scope.read_payouts.titleLaunch': '利用料と前払い残高を見る',
  'mcpAuth.scope.read_payouts.descLaunch': '月別・日別の利用料、前払いの残高（招待クレジットと残高が少ないときのお知らせを含む）',
  'mcpAuth.scope.write_config.title': 'プロジェクトの設定を変える',
  'mcpAuth.scope.write_config.desc': 'kumo.config.json の反映（本番への反映は確認あり）、投稿用の計測つき共有リンクの作成、独自ドメインの追加・削除（確認あり）',
  'mcpAuth.scope.write_players.title': '利用者の BAN / BAN の解除',
  'mcpAuth.scope.write_players.desc': '利用者の BAN・BAN の解除、異議申し立てへの回答（毎回確認あり）',
  'mcpAuth.scope.deploy.title': '公開とデプロイの切り替え',
  'mcpAuth.scope.deploy.desc': 'プロジェクトの作成、アプリのファイルのアップロード、デプロイしたバージョンの公開・巻き戻し（本番での公開は確認あり）',
  'mcpAuth.scope.read_functions.title': 'サーバー関数を見る',
  'mcpAuth.scope.read_functions.desc': '{product} Functions（自分のサーバーのコード）の状態、バージョン、データベース、定期実行、シークレットの「名前」。シークレットの値は表示されません',
  'mcpAuth.scope.read_logs.title': 'サーバー関数のログを読む',
  'mcpAuth.scope.read_logs.desc': '{product} Functions のコンソール出力（直近 7 日・自分のコードが書き出したもの）',
  'mcpAuth.scope.write_functions.title': 'サーバー関数を管理する',
  'mcpAuth.scope.write_functions.desc': '有効化、デプロイ、シークレットの設定、関数のデータベースでの SQL の実行、削除（本番の変更とすべての削除は確認あり）',
  'mcpAuth.scope.write_mail.title': 'ユーザーにメールを送る',
  'mcpAuth.scope.write_mail.desc': '受信に同意したユーザーへのメールの下書き・送信・予約・定期配信、予約の取り消し。送信・予約・定期配信は毎回確認あり（宛先・件名・日時）',
  'mcpAuth.scope.payouts_request.title': '出金を申請する',
  'mcpAuth.scope.payouts_request.desc': '出金の申請を作成します。実行されるのは、{product} のサイトでパスキーで承認した後だけです',
  // 2026-10-15 console/preview-name（founder 10/15）: 名前は {preview} = 「KUMODeck Preview (v0.2)」で ja でも英語のまま
  //   （Academy の YouTube は画面に日本語を出さない決まり）。「staging」「検証用」「先取り」を名前に使わない。説明の文は日本語でよい。
  //   note はバッジ（太字の名前）のすぐ後に続けて出る = 名前を繰り返さない（en.mcpAuth.ts と同じ WHY）
  'mcpAuth.staging.badge': '{preview}',
  'mcpAuth.staging.note': 'テスト専用の接続です。テスト用のアカウントとテスト用のプロジェクトでだけ使えます。',
  // 開いた形（request.stagingOpen・2026-10-15 core/staging-open）: アカウントがあれば誰でも・本人のプロジェクト。「テスト専用」と言わない。
  //   何が違うか（新しい機能が先・変わりうる）と、変わらないこと（本人のプロジェクトだけ・前払いから）を具体的に言う
  'mcpAuth.stagingOpen.badge': '{preview}',
  'mcpAuth.stagingOpen.note': '{product} の新しい機能を先に試せる接続です。いちばん新しい機能（Blender や AI の 3D モデルなど）が先に入っていて、これから変わることがあります。触れるのはあなた自身のプロジェクトだけです。お金のかかる機能は、ふつうの接続と同じく {product} の残高から払われます。',
  'error.staging_not_allowed': 'これは {preview} の接続です（テスト専用）。このアカウントかプロジェクトは、テスト用の一覧にありません。'
};
